import { clearAuthTokens } from "./token-storage";
import { runtimeConfig } from "./runtime-config";
import type { BillingPlanId } from "@quire/config";
export type Platform = "web" | "ios" | "android" | "desktop";

export type AuthUser = {
    id: string;
    email: string;
    name: string;
    emailVerified: boolean;
    image?: string | null;
};

export type AuthSessionEnvelope = {
    user: AuthUser;
    organizationId?: string | null;
    workspaceId?: string | null;
    workspaceName?: string | null;
    role?: "owner" | "admin" | "member";
    plan?: BillingPlanId;
    effectivePlan?: BillingPlanId;
    isActive?: boolean;
    isReadOnly?: boolean;
    canUsePaidMessaging?: boolean;
    status?: "inactive" | "incomplete" | "incomplete_expired" | "trialing" | "active" | "past_due" | "canceled" | "unpaid" | "revoked" | "expired";
    accessLevel?: "full" | "read_only" | "blocked";
    trialEndsAt?: string | null;
    trialState?: "active" | "expired" | "no_trial" | "none";
    projectId?: string | null;
    projectPlatform?: Platform | null;
    onboardingStep?: number;
    onboardingComplete?: boolean;
};

export type OtpAccountHints = {
    accountExists: boolean;
    linkedMethods: Array<"otp" | "google">;
    suggestedMethod: "otp" | "google";
};

type ApiResult<T> = {
    data: T | null;
    error: string | null;
    status: number;
    errorCode: string | null;
    requestId: string | null;
};

function getApiBaseUrl(): string {
    return runtimeConfig.apiUrl;
}

function buildUrl( path: string ): string {
    const base = getApiBaseUrl().trim();
    if ( !base ) return path;
    try {
        return new URL( path, base ).toString();
    } catch {
        return `${ base }${ path }`;
    }
}

function normalizeHeaderRecord( input?: HeadersInit ): Record<string, string> {
    if ( !input ) {
        return {};
    }

    const headers = new Headers( input );
    const normalized: Record<string, string> = {};
    headers.forEach( ( value, key ) => {
        normalized[key] = value;
    } );

    return normalized;
}

async function authRequest<T>(
    path: string,
    init?: RequestInit
): Promise<ApiResult<T>> {
    const requestUrl = buildUrl( path );
    const headers = normalizeHeaderRecord( init?.headers );

    const executeRequest = ( url: string ) =>
        fetch( url, {
            credentials: "include",
            ...init,
            headers
        } );

    try {
        const response = await executeRequest( requestUrl );

        const text = await response.text();
        let payload: Record<string, unknown> = {};
        if ( text ) {
            try {
                payload = JSON.parse( text ) as Record<string, unknown>;
            } catch {
                payload = {};
            }
        }

        if ( !response.ok ) {
            const errorCode = typeof payload.error === "string" ? payload.error : null;
            const requestId = typeof payload.requestId === "string" ? payload.requestId : null;
            const error =
                errorCode
                    ? errorCode
                    : typeof payload.message === "string"
                        ? payload.message
                        : `Request failed with status ${ response.status }.`;
            return {
                data: null,
                error,
                status: response.status,
                errorCode,
                requestId
            };
        }

        return {
            data: payload as T,
            error: null,
            status: response.status,
            errorCode: null,
            requestId: null
        };
    } catch ( error ) {
        if ( import.meta.env.DEV && requestUrl !== path ) {
            try {
                const retry = await executeRequest( path );
                const retryText = await retry.text();
                let retryPayload: Record<string, unknown> = {};
                if ( retryText ) {
                    try {
                        retryPayload = JSON.parse( retryText ) as Record<string, unknown>;
                    } catch {
                        retryPayload = {};
                    }
                }

                if ( !retry.ok ) {
                    const retryErrorCode = typeof retryPayload.error === "string" ? retryPayload.error : null;
                    const retryRequestId = typeof retryPayload.requestId === "string" ? retryPayload.requestId : null;
                    const retryError =
                        retryErrorCode
                            ? retryErrorCode
                            : typeof retryPayload.message === "string"
                                ? retryPayload.message
                                : `Request failed with status ${ retry.status }.`;
                    return {
                        data: null,
                        error: retryError,
                        status: retry.status,
                        errorCode: retryErrorCode,
                        requestId: retryRequestId
                    };
                }

                return {
                    data: retryPayload as T,
                    error: null,
                    status: retry.status,
                    errorCode: null,
                    requestId: null
                };
            } catch {
                // Surface the original network error below.
            }
        }

        const detail = error instanceof Error ? error.message : "Network request failed.";
        return {
            data: null,
            error: `Network request failed for ${ requestUrl }: ${ detail }`,
            status: 0,
            errorCode: null,
            requestId: null
        };
    }
}

export const authApi = {
    requestOtp( input: { email: string; name?: string; turnstileToken?: string; referralCode?: string } ) {
        return authRequest<{
            otpSent: boolean;
            message?: string;
            code?: string;
            flow?: "signup" | "login";
            accountHints?: OtpAccountHints;
        }>(
            "/api/auth/request-otp",
            {
                method: "POST",
                headers: {
                    "content-type": "application/json"
                },
                body: JSON.stringify( input )
            }
        );
    },

    verifyOtp( input: {
        email: string;
        name?: string;
        code: string;
        deviceFingerprint?: string;
        referralCode?: string;
    } ) {
        return authRequest<AuthSessionEnvelope | { requires2fa: true; interimToken: string }>(
            "/api/auth/verify-otp",
            {
                method: "POST",
                headers: {
                    "content-type": "application/json"
                },
                body: JSON.stringify( input )
            }
        );
    },

    verifyTotp( input: {
        interimToken: string;
        code: string;
    } ) {
        return authRequest<AuthSessionEnvelope>(
            "/api/auth/verify-totp",
            {
                method: "POST",
                headers: {
                    "content-type": "application/json"
                },
                body: JSON.stringify( input )
            }
        );
    },

    oneTap( credential: string ) {
        return authRequest<AuthSessionEnvelope | { requires2fa: true; interimToken: string }>(
            "/api/auth/google/onetap",
            {
                method: "POST",
                headers: {
                    "content-type": "application/json"
                },
                body: JSON.stringify( { credential } )
            }
        );
    },

    requestTotpRecovery( interimToken: string ) {
        return authRequest<{ otpSent: boolean; message?: string }>(
            "/api/auth/totp-recovery/request",
            {
                method: "POST",
                headers: {
                    "content-type": "application/json"
                },
                body: JSON.stringify( { interimToken } )
            }
        );
    },

    verifyTotpRecovery( interimToken: string, code: string ) {
        return authRequest<AuthSessionEnvelope & { totpRecoveryUsed?: boolean }>(
            "/api/auth/totp-recovery/verify",
            {
                method: "POST",
                headers: {
                    "content-type": "application/json"
                },
                body: JSON.stringify( { interimToken, code } )
            }
        );
    },

    getCurrentSession() {
        return authRequest<AuthSessionEnvelope>( "/api/auth/me", { method: "GET" } );
    },

    refreshSession() {
        return authRequest<AuthSessionEnvelope>( "/api/auth/refresh", {
            method: "POST"
        } );
    },

    switchWorkspace( input: { workspaceId: string } ) {
        return authRequest<AuthSessionEnvelope>( "/api/auth/switch-workspace", {
            method: "POST",
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify( input )
        } );
    },

    logout() {
        clearAuthTokens();
        return authRequest<{ success: boolean }>( "/api/auth/logout", {
            method: "POST"
        } );
    },

    requestStepUp( input?: { action?: string } ) {
        return authRequest<{ stepUpRequested: boolean; code?: string; expiresInSeconds?: number }>(
            "/api/auth/step-up/request",
            {
                method: "POST",
                headers: {
                    "content-type": "application/json"
                },
                body: JSON.stringify( input ?? {} )
            }
        );
    },

    verifyStepUp( input: { code: string; action?: string; method?: "email" | "totp" } ) {
        return authRequest<{ stepUpVerified: boolean; stepUpToken?: string; expiresAt?: string }>(
            "/api/auth/step-up/verify",
            {
                method: "POST",
                headers: {
                    "content-type": "application/json"
                },
                body: JSON.stringify( input )
            }
        );
    },

    googleLoginUrl(
        deviceFingerprint?: string | null,
        turnstileToken?: string | null,
        referralCode?: string | null
    ) {
        const absoluteApiBase = runtimeConfig.redirectApiUrl.trim();
        const fallbackBase = typeof window !== "undefined" ? window.location.origin : "http://localhost:8787";
        const url = new URL( "/api/auth/google", absoluteApiBase || fallbackBase );
        if ( typeof window !== "undefined" ) {
            url.searchParams.set( "appOrigin", window.location.origin );
        }
        if ( deviceFingerprint?.trim() ) {
            url.searchParams.set( "deviceFingerprint", deviceFingerprint.trim() );
        }
        if ( turnstileToken?.trim() ) {
            url.searchParams.set( "turnstileToken", turnstileToken.trim() );
        }
        if ( referralCode?.trim() ) {
            url.searchParams.set( "referralCode", referralCode.trim() );
        }

        return url.toString();
    }
};

export const clearStoredAuthTokens = clearAuthTokens;
